import { createContext, useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMe, login as apiLogin, logout as apiLogout } from "../api/auth";
import { setOnUnauthorized } from "../api/client";
import type { User } from "../types/user";

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Clear all service worker caches. Best-effort — callers proceed regardless.
 * Used on both explicit logout and session expiry (401) to prevent
 * financial data leakage on shared devices.
 */
async function clearServiceWorkerCaches(): Promise<void> {
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch {
    // Cache clearing failed — proceed with session teardown anyway
  }
}

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    // Fire-and-forget cache clear on session expiry.
    // The new SW never caches API data, but this is defense-in-depth
    // for static assets and prevents inconsistency with the explicit
    // logout path which also clears caches.
    void clearServiceWorkerCaches();
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    setOnUnauthorized(handleUnauthorized);
  }, [handleUnauthorized]);

  useEffect(() => {
    let cancelled = false;

    async function checkAuth(): Promise<void> {
      try {
        const currentUser = await fetchMe();
        if (!cancelled) {
          setUser(currentUser);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void checkAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const loggedInUser = await apiLogin(email, password);
    setUser(loggedInUser);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
    } catch {
      // Server-side session cleanup failed — proceed with local cleanup.
      // If the server is unreachable, we still clear local state and caches
      // to prevent financial data leakage on shared devices.
    }
    await clearServiceWorkerCaches();
    setUser(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}
